import { useRef } from 'react';
import { useButton } from 'react-aria';
import PropTypes from 'prop-types';

import styles from './SortButton.module.scss';
import Icon from '@mdi/react';
import { mdiSortDescending, mdiSortAscending } from '@mdi/js';
import { Link, useLocation } from 'react-router';
import { split } from 'lodash';

const sortToObject = (string) => {
    const [column, order] = string ? split(string, ',') : ['name', 'asc'];
    return {
        column,
        order
    };
};

const paramsToObject = (entries) => {
    const result = {};
    for (const [key, value] of entries) {
        result[key] = (key === 'sort') ? sortToObject(value) : value;
    }
    return result;
};

const SortButton = (props) => {
    const { children, column } = props;

    const location = useLocation();

    const ref = useRef();
    const { buttonProps, isPressed } = useButton({
        ...props,
        elementType: 'span'
    }, ref);

    const linkToInvertedOrder = () => {
        const params = new URLSearchParams(location?.search);
        const order = (getSortOrder() === 'asc') ? 'desc' : 'asc';
        params.set('sort', `${column},${order}`);
        return location?.pathname + '?' + params.toString();
    };

    const getSortOrder = () => {
        const params = new URLSearchParams(location?.search);
        const { sort } = paramsToObject(params.entries());
        if (column === sort?.column) {
            return sort?.order === 'asc' ? 'asc' : 'desc';
        } else {
            // if no sort specified then return opposite of default
            // so that when we invert it, it uses the default.
            return props.defaultSortOrder === 'asc' ? 'desc' : 'asc';
        }
    };

    const colIsSorted = () => {
        const params = new URLSearchParams(location?.search);
        const { sort } = paramsToObject(params.entries());
        return (column === sort?.column);
    };

    return (
        <Link
            {...buttonProps}
            to={linkToInvertedOrder()}
            className={['govuk-body-m govuk-!-font-weight-bold', styles.button, isPressed && styles.pressed].join(' ')}
            ref={ref}
        >
            {children}
            {colIsSorted() &&
                <span className={styles.icon} aria-hidden="true">
                    {
                        /* the mdiSort icons seem to be mislabelled or at least unclear
                        they look better swapped. */
                        // TODO: make SortButton obsolete and replace tables in bookmarks and I2D pages with react-aria/useTable + sorting
                    }
                    <Icon path={getSortOrder() === 'asc' ? mdiSortDescending : mdiSortAscending} size={1}/>
                </span>
            }
        </Link>
    );
};

SortButton.propTypes = {
    defaultSortOrder: PropTypes.oneOf(['asc', 'desc']),
    children: PropTypes.any.isRequired,
    column: PropTypes.string
};

SortButton.defaultProps = {
    defaultSortOrder: 'asc'
};

export default SortButton;
